/**
 * PresenceCube — the resting cube of Liquid Glass at the origin.
 *
 * Optically clear, substantial. Not frosted, not sci-fi. During the
 * `fluidizing` and `separating` phases the cube gains a subtle roughness
 * drop and micro-scale wobble — enough to read as "losing solidity" but
 * quiet enough to feel physical, not theatrical. On the reverse (merging)
 * it re-settles.
 *
 * The cube itself never leaves its position. A drop pinches off from its
 * right face; that drop is rendered by JourneyVessel.
 */
import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RoundedBox, MeshTransmissionMaterial } from "@react-three/drei";
import { phaseProgress, easeSettle } from "./LiquidGlassTransition";

export default function PresenceCube({ transition }) {
  const meshRef = useRef();

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const now = performance.now();
    const p = phaseProgress(transition.phase, transition.phaseStartMs, now);
    const eased = easeSettle(p);
    const t = clock.getElapsedTime();

    // Base scale.  A tiny breathing at rest (0.5% amplitude, 8s period).
    let sx = 1, sy = 1, sz = 1;
    const breathe = 1 + Math.sin(t * (Math.PI * 2) / 8) * 0.005;

    if (transition.phase === "resting" || transition.phase === "stable") {
      sx = sy = sz = breathe;
    } else if (transition.phase === "fluidizing") {
      // Very subtle "gel" wobble — sinusoidal at 3Hz, 1.5% amplitude, ramps in.
      const wob = Math.sin(t * Math.PI * 2 * 3) * 0.015 * eased;
      sx = 1 + wob;
      sy = 1 - wob;
      sz = 1 + Math.cos(t * Math.PI * 2 * 3) * 0.015 * eased;
    } else if (transition.phase === "separating") {
      // Slight inward pinch on the +x face while the drop pulls away.
      sx = 1 - 0.04 * eased;
      sy = 1 + 0.02 * eased;
      sz = 1 + 0.02 * eased;
    } else if (transition.phase === "merging") {
      // Reverse pinch as the drop is absorbed.
      const wob = 0.03 * (1 - eased);
      sx = 1 - wob;
      sy = 1 + wob * 0.5;
      sz = 1 + wob * 0.5;
    } else {
      sx = sy = sz = breathe;
    }

    meshRef.current.scale.set(sx, sy, sz);
  });

  return (
    <group position={transition.SCENE.cubePosition}>
      <RoundedBox
        ref={meshRef}
        args={[transition.SCENE.cubeSize, transition.SCENE.cubeSize, transition.SCENE.cubeSize]}
        radius={0.16}
        smoothness={5}
        creaseAngle={0.4}
      >
        <MeshTransmissionMaterial
          transmission={1}
          thickness={1.1}
          roughness={0.02}
          ior={1.5}
          chromaticAberration={0.05}
          anisotropy={0.06}
          distortion={0.18}
          distortionScale={0.4}
          temporalDistortion={0.04}
          attenuationDistance={2.2}
          attenuationColor="#f6f4ef"
          background="#00000000"
          clearcoat={0.85}
          clearcoatRoughness={0.04}
        />
      </RoundedBox>
    </group>
  );
}
